import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  CriarCustoAquisicaoDto,
  CustoAquisicaoResponse,
  EnfileiradoNoLoteResponse,
  ListarCustosAquisicaoQuery,
  SimularCustoAquisicaoDto,
  SimularCustoAquisicaoResponse,
} from '@triade/contracts';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

/** `ativo` false = nao consulta (ex.: sem custo.ver a API responde 403). */
export function useCustosAquisicao(q: Partial<ListarCustosAquisicaoQuery> = {}, opts: { ativo?: boolean } = {}) {
  return useConsulta<CustoAquisicaoResponse[]>(['custos-aquisicao'], '/custos-aquisicao', { query: q, ...(opts.ativo === undefined ? {} : { ativo: opts.ativo }) });
}

/**
 * Calculo ao vivo do custo de aquisicao (POST /custos-aquisicao/simular): nao grava. A tela so envia o corpo e mostra a
 * resposta; `corpo` null = nao consulta (formulario invalido). O mesmo corpo repetido e servido do cache do TanStack Query.
 */
export function useSimularCustoAquisicao(corpo: SimularCustoAquisicaoDto | null, opts: { ativo?: boolean } = {}) {
  const { empresaId } = useSessao();
  return useQuery<SimularCustoAquisicaoResponse, ApiError>({
    queryKey: ['custos-aquisicao', 'simular', empresaId, corpo],
    queryFn: () => requisitar<SimularCustoAquisicaoResponse>('/custos-aquisicao/simular', { method: 'POST', body: corpo }),
    enabled: corpo !== null && empresaId !== null && (opts.ativo ?? true),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Resultado do POST: 201 = vigencia gravada; 202 = entrou no lote de atualizacao de custos (COM_APROVACAO), ainda nao vale. */
export type ResultadoCriarCustoAquisicao = CustoAquisicaoResponse | EnfileiradoNoLoteResponse;

export function ehEnfileirado(r: ResultadoCriarCustoAquisicao): r is EnfileiradoNoLoteResponse {
  return 'enfileiradas' in r;
}

export function useCriarCustoAquisicao() {
  return useMutacao<ResultadoCriarCustoAquisicao, CriarCustoAquisicaoDto>({
    metodo: 'POST', caminho: () => '/custos-aquisicao', corpo: (v) => v, invalidar: [['custos-aquisicao'], ['itens']],
  });
}
